import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLeaderboard, type LeaderboardPeriod } from '@/lib/api/reports.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';

const periods: { id: LeaderboardPeriod; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
];

function medal(rank: number) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

function trendIcon(trend: 'up' | 'down' | 'same') {
  if (trend === 'up') return '↑';
  if (trend === 'down') return '↓';
  return '→';
}

export function LeaderboardPage() {
  const [period, setPeriod] = useState<LeaderboardPeriod>('week');

  const q = useQuery({
    queryKey: ['reports-leaderboard', period],
    queryFn: () => getLeaderboard(period),
  });

  if (q.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading leaderboard…</div>;
  }

  if (q.isError) {
    const parsed = parseApiError(q.error);
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">
        {parsed.message || 'Could not load leaderboard.'}
      </div>
    );
  }

  const data = q.data;
  const rankings = data?.rankings ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {periods.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPeriod(p.id)}
            className={[
              'rounded-lg px-3 py-1.5 text-sm font-semibold',
              period === p.id ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
            ].join(' ')}
          >
            {p.label}
          </button>
        ))}
      </div>

      {data?.storeStats ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase text-slate-500">Tickets Completed</p>
            <p className="mt-1 text-2xl font-black text-slate-950">{data.storeStats.totalTicketsCompleted}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase text-slate-500">Revenue</p>
            <p className="mt-1 text-2xl font-black text-slate-950">
              {formatCurrency(data.storeStats.totalRevenue)}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase text-slate-500">Avg NPS</p>
            <p className="mt-1 text-2xl font-black text-slate-950">
              {data.storeStats.avgNpsScore ?? '—'}
            </p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {rankings.map((tech) => (
          <div
            key={tech.userId}
            className={[
              'rounded-2xl border bg-white p-5 shadow-sm',
              tech.rank <= 3 ? 'border-primary/30 ring-1 ring-primary/10' : 'border-slate-200',
            ].join(' ')}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-lg font-bold text-white">
                  {tech.avatarInitials}
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-950">
                    {tech.firstName} {tech.lastName}
                  </p>
                  <p className="text-sm text-slate-500">
                    {medal(tech.rank)} · {tech.ticketsCompleted} completed
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-black text-primary">{tech.performanceScore}</p>
                <p className="text-xs font-semibold text-slate-500">
                  {trendIcon(tech.trend)} score
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-600">
              <span>In progress: {tech.ticketsInProgress}</span>
              <span>Avg hrs: {tech.avgResolutionHours.toFixed(1)}</span>
              <span>Billable: {tech.billableLaborMinutes} min</span>
              <span>NPS: {tech.npsAvgScore?.toFixed(1) ?? '—'}</span>
              <span>Warranty: {tech.warrantyCallbacks}</span>
              <span>Labor: {tech.totalLaborMinutes} min</span>
            </div>
          </div>
        ))}
      </div>

      {!rankings.length ? (
        <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
          No technician activity in this period.
        </p>
      ) : null}
    </div>
  );
}
